import axios from './axios';

// ─── PKCE helpers (Web Crypto API — browser only) ────────────────────────────

function base64UrlEncode(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    bytes.forEach(b => { binary += String.fromCharCode(b); });
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(digest);
}

// ─── Google Sign-In via Cognito Hosted UI ────────────────────────────────────

export async function getGoogleSignInUrl(intent = 'login') {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_WEB_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;

    // CSRF state
    const stateArray = new Uint8Array(16);
    crypto.getRandomValues(stateArray);
    const state = base64UrlEncode(stateArray);

    // PKCE
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Persist for callback verification
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_intent', intent);
    sessionStorage.setItem('oauth_code_verifier', codeVerifier);

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,
        identity_provider: 'Google',
        scope: 'openid email profile',
        state,
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
    });

    return `${domain}/oauth2/authorize?${params.toString()}`;
}

// Exchange authorization code for Cognito tokens (public client — no secret)
export async function exchangeCodeForTokens(code) {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_WEB_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier');

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        redirect_uri: redirectUri,
        code,
        code_verifier: codeVerifier,
    });

    // Use raw fetch — this goes to Cognito domain, not our API
    const response = await fetch(`${domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || errorData.error || 'Token exchange failed');
    }

    return response.json(); // { access_token, id_token, refresh_token, ... }
}

// ─── Apple Sign-In via Apple JS SDK ─────────────────────────────────────────

export async function loginWithApple() {
    const nonceArray = new Uint8Array(32);
    crypto.getRandomValues(nonceArray);
    const nonce = base64UrlEncode(nonceArray);

    // Hash nonce to hex — must match BE's crypto.createHash('sha256').digest('hex')
    const encoder = new TextEncoder();
    const digest = await crypto.subtle.digest('SHA-256', encoder.encode(nonce));
    const hashedNonce = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');

    return new Promise((resolve, reject) => {
        if (!window.AppleID) {
            reject(new Error('Apple Sign-In SDK not loaded'));
            return;
        }

        window.AppleID.auth.init({
            clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
            scope: 'email name',
            redirectURI: window.location.origin,
            usePopup: true,
            nonce: hashedNonce,
        });

        window.AppleID.auth.signIn()
            .then(response => {
                resolve({ identityToken: response.authorization.id_token, nonce });
            })
            .catch(err => {
                if (err.error === 'popup_closed_by_user') {
                    reject(new Error('Sign-in cancelled'));
                } else {
                    reject(new Error(err.error || 'Apple sign-in failed'));
                }
            });
    });
}

export async function socialLoginWithApple() {
    const { identityToken, nonce } = await loginWithApple();
    const response = await axios.post('/auth/social', {
        provider: 'apple',
        identityToken,
        nonce,
    });
    return { ...response.data, _identityToken: identityToken, _nonce: nonce };
}

export async function linkAppleAccount() {
    const { identityToken, nonce } = await loginWithApple();
    return linkSocialProvider('apple', identityToken, nonce);
}

// Complete social signup (pick username for new Google user — web flow)
export async function completeSocialSignup(provider, username) {
    try {
        const response = await axios.post('/auth/social/complete-web', { provider, username });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
}

// Link a social provider to existing account
// source: 'cognito' for web OAuth flow, omit for iOS native tokens
export async function linkSocialProvider(provider, identityToken, nonce, source) {
    try {
        const response = await axios.post('/auth/link-social', { provider, identityToken, nonce, source });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
}

// Unlink a social provider from account
export async function unlinkSocialProvider(provider) {
    try {
        const response = await axios.post('/auth/unlink-social', { provider });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
}

export const registerUser = async (username, email, password) => {
    try {
        const response = await axios.post('/users', { username, email, password });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const loginUser = async (username, password) => {
    try {
        const response = await axios.post('/auth/login', { username, password });
        //console.log('loginUser - response:', response.data); // Add logging here
        return response.data;
    } catch (error) {
        console.error('loginUser - error:', error.response?.data || error.message);
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const logoutUser = async () => {
    try {
        const authOrigin = localStorage.getItem('authOrigin');

        if (authOrigin === 'cognito-oauth') {
            // OAuth users: no server-side Cognito session to invalidate.
            // Just clear local tokens (handled by caller).
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            return;
        }
        await axios.post('/auth/logout', { accessToken });
        localStorage.removeItem('accessToken');
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const forgotPassword = async (username) => {
    try {
        const response = await axios.post('/auth/forgot-password', { username });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const confirmForgotPassword = async (username, code, newPassword) => {
    try {
        const response = await axios.post('/auth/confirm-forgot-password', { username, code, newPassword });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const fetchUserDetails = async accessToken => {
    try {
        //console.log('fetchUserDetails - accessToken:', accessToken);
        const response = await axios.get('/users/me', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        //console.log('fetchUserDetails - response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch user details:', error);
        return null;
    }
};
